import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Building2, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Users, 
  UserCircle,
  MoreVertical,
  ChevronRight,
  Info
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function DepartementsManagement() {
  const { hasPermission } = usePermissions();
  const [depts, setDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [deptRes, empRes] = await Promise.all([
        axios.get(`${API}/rh/departements`, { headers: headers() }),
        axios.get(`${API}/rh/employes`, { headers: headers() })
      ]);
      setDepts(deptRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDepts = useMemo(() => {
    return depts.filter(d => d.nom.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [depts, searchTerm]);

  const handleAddDept = () => {
    Swal.fire({
      title: 'Nouveau Département',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Nom du département *</label>
            <input id="d-nom" class="rh-modal-input" placeholder="Ex: Direction Technique">
          </div>
          <div class="rh-input-group">
            <label>Responsable RH assigné</label>
            <select id="d-resp" class="rh-modal-input">
              <option value="">Sélectionner...</option>
              ${employees.map(e => `<option value="${e._id}">${e.prenom} ${e.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Description</label>
            <textarea id="d-desc" class="rh-modal-input" placeholder="Objectifs et périmètre..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Créer',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const nom = document.getElementById('d-nom').value;
        const responsable = document.getElementById('d-resp').value;
        const description = document.getElementById('d-desc').value;
        if (!nom) {
          Swal.showValidationMessage('Le nom est obligatoire');
          return false;
        }
        return { nom, responsable, description };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(`${API}/rh/departements`, result.value, { headers: headers() });
          Swal.fire('Succès', 'Département créé !', 'success');
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible de créer le département.', 'error');
        }
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({ title: 'Supprimer ?', icon: 'warning', showCancelButton: true }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(`${API}/rh/departements/${id}`, { headers: headers() });
        fetchData();
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ background: 'var(--rh-blue-dark)', width: '45px', height: '45px', borderRadius: '12px' }}>
              <Building2 size={24} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Structure Organisationnelle</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>Gérez vos départements et pôles</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="rh-search-wrapper" style={{ width: '250px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddDept}>
              <Plus size={18} /> Nouveau
            </button>
          </div>
        </div>

        <div className="rh-table-container">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Département</th>
                <th>Responsable RH</th>
                <th>Effectifs</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepts.map(d => (
                <tr key={d._id}>
                  <td>
                    <div className="rh-user-cell">
                      <div className="rh-avatar" style={{ background: '#EBF4FF', color: '#3182CE' }}>
                        <Building2 size={16} />
                      </div>
                      <span className="rh-user-name" style={{ fontWeight: '700' }}>{d.nom}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <UserCircle size={14} color="var(--rh-text-muted)" />
                      <span style={{ fontSize: '13px' }}>{d.responsable ? `${d.responsable.prenom} ${d.responsable.nom}` : 'Non assigné'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="rh-status-badge info" style={{ gap: '6px' }}>
                      <Users size={12} /> {d.nbEmployes || 0} employés
                    </div>
                  </td>
                  <td>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.description || '—'}
                    </p>
                  </td>
                  <td>
                    <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                      <button className="rh-action-btn edit" onClick={() => Swal.fire('Infos', d.description || 'Aucune description.', 'info')}>
                        <Info size={16} />
                      </button>
                      <button className="rh-action-btn delete" onClick={() => handleDelete(d._id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
